import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { WarrantyReceiptListFields } from '../../common/enums.js';
import { downloadWarrantyReceiptPdf } from '../../common/fetch.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  companyMsg,
  dateMsg,
  deviceMsg,
  downloadWarrantyReceiptMsg,
  idNumberMsg,
  noDevicesMsg,
  t,
  technicalErrorMsg,
  userMsg,
  warrantyReceiptNotAvailableMsg,
  warrantyReceiptsIncompleteListMsg,
} from '../../common/i18n';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils.js';
import { type DownloadError, downloadFile } from '../../common/utils/domUtils.js';
import { isDefined } from '../../common/utils/objectUtils';
import { highlightKeyword } from '../../common/utils/searchFieldUtils';
import { truncateText } from '../../common/utils/stringUtils.js';
import { ConsolidatedListView } from '../ConsolidatedListView/ConsolidatedListView.js';
import { Highlight } from '../Highlight/Highlight.js';
import { SubscriptionHiddenFields } from '../SubscriptionLists/SubscriptionHiddenFields';
import { SubscriptionLink } from '../SubscriptionLists/SubscriptionLink';
import { getTypeFilterGroup } from '../Subscriptions/SubscriptionsDeviceFilters';
import { DEFAULT_ITEMS_PER_PAGE, ListPagination, ListSearch, SearchFilters, Table } from '../Table';
import { setDefaultItemsPerPage } from '../Table/tableUtils.js';
import type { CLTableColumn } from '../../common/types/componentLibrary';
import type { SubscriptionAggregationsResponse, SubscriptionSearchResponse } from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';
import type { SubscriptionDeviceTableSearchParams } from '../SubscriptionLists/SubscriptionTable';
import type { SearchFilterGroup } from '../Table/SearchFilters';
import type { JSX } from 'react';

const truncateTextMaxChars = 100;

export interface WarrantyReceiptListWithTableProps {
  warrantyReceipts?: SubscriptionSearchResponse[];
  aggregations?: SubscriptionAggregationsResponse;
  totalItems: number;
  authenticatedUser?: AuthenticatedUser;
}

export const WarrantyReceiptList = ({
  warrantyReceipts,
  aggregations,
  totalItems,
  authenticatedUser,
}: WarrantyReceiptListWithTableProps): JSX.Element => {
  const searchParams = useSearchParams<SubscriptionDeviceTableSearchParams>();
  const { search, subscriptionSubType } = searchParams;
  const searchTerms = search?.split(' ') || [];
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [downloadError, setDownloadError] = useState<DownloadError>(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const downloadWarrantyReceipt = async (deliveryOrderId: string) => {
    setDownloadError(false);
    setDownloadingId(deliveryOrderId);
    const error = await downloadFile(
      () => downloadWarrantyReceiptPdf(deliveryOrderId),
      `warranty-receipt-${deliveryOrderId}.pdf`
    );
    setDownloadingId(null);
    setDownloadError(error);
  };

  const columns: CLTableColumn[] = [
    {
      key: WarrantyReceiptListFields.SUBSCRIPTION_NAME,
      label: t.TPVQ(deviceMsg),
      sortable: true,
      width: '20%',
    },
    {
      key: WarrantyReceiptListFields.CREATED,
      label: t.XFUZ(dateMsg),
      sortable: true,
      width: '10%',
    },
    {
      key: WarrantyReceiptListFields.SUBSCRIPTION_DISPLAY_ID,
      label: t.YTH3(idNumberMsg),
      width: '15%',
    },
    {
      key: WarrantyReceiptListFields.SUBSCRIPTION_CONTACT_NAME,
      label: t.XRDP(userMsg),
      sortable: true,
      width: '20%',
    },
    {
      key: WarrantyReceiptListFields.COMPANY_NAME,
      label: t.KJTS(companyMsg),
      width: '10%',
    },
    {
      key: WarrantyReceiptListFields.DOWNLOAD_WARRANTY_RECEIPT,
    },
  ];

  const rows = warrantyReceipts?.map(value => ({
    [WarrantyReceiptListFields.SUBSCRIPTION_NAME]: (
      <SubscriptionLink
        subscription={value.result}
        path={paths.PS_DEVICE}
        search={search}
        withoutIcon={true}
        disabled={value.result.billingAccountRecordType === 'CardPayment'}
      >
        <SubscriptionHiddenFields
          subscriptionHeader={value.result}
          search={search}
          matchedFields={value.matchedFields}
        />
        {value.result.serialNumber && (
          <div className={`${dsClass.FONT_SIZE_SMALL} ${dsClass.COLOR_BLUE_800}`}>
            IMEI {highlightKeyword(value.result.serialNumber, search)}
          </div>
        )}
      </SubscriptionLink>
    ),
    [WarrantyReceiptListFields.CREATED]: formatTimestampToDDMMYYYY(value.result.created),
    [WarrantyReceiptListFields.SUBSCRIPTION_DISPLAY_ID]: (
      <Highlight text={value.result.subscriptionDisplayId ?? ''} highlights={searchTerms} />
    ),
    [WarrantyReceiptListFields.SUBSCRIPTION_CONTACT_NAME]: (
      <Highlight
        text={truncateText(value.result.subscriptionContactName || '', truncateTextMaxChars)}
        highlights={searchTerms}
      />
    ),
    [WarrantyReceiptListFields.COMPANY_NAME]: getCompanyName(authenticatedUser, value.result.accountMasterId),
    [WarrantyReceiptListFields.DOWNLOAD_WARRANTY_RECEIPT]: value.result.deliveryOrderId && (
      <CL.Button
        className={`${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER} ${dsClass.PADDING_LEFT_0} ${dsClass.BACKGROUND_COLOR_TRANSPARENT} ${dsClass.BORDER_STYLE_NONE}`}
        color="link"
        size="s"
        loading={downloadingId === value.result.deliveryOrderId}
        icon={<CL.Icon className={`${dsClass.MARGIN_RIGHT_2}`} icon="download" type="filled" />}
        onClick={() => downloadWarrantyReceipt(value.result.deliveryOrderId!)}
      >
        {t.IB0X(downloadWarrantyReceiptMsg)}
      </CL.Button>
    ),
  }));

  const deviceFilterGroups: SearchFilterGroup[] = [
    getTypeFilterGroup(subscriptionSubType, aggregations?.deviceAggregations?.subscriptionSubType),
  ].filter(isDefined);

  return (
    <div className="of-warranty-receipt-list">
      <CL.NotificationBanner
        className={dsClass.MARGIN_BOTTOM_3}
        text={t.WRBN(warrantyReceiptsIncompleteListMsg)}
        icon={<CL.Icon icon="information" size="m" type="regular" color="blue-600" />}
      />
      {downloadError && (
        <CL.Notification
          type="error"
          text={downloadError === 'notFound' ? t.Q8W5(warrantyReceiptNotAvailableMsg) : t.T4OU(technicalErrorMsg)}
          visible={true}
        />
      )}
      <ConsolidatedListView
        authenticatedUser={authenticatedUser}
        listElement={<Table columns={columns} noItemsText={t.CD0S(noDevicesMsg)} rows={rows || []} />}
        listPagination={<ListPagination totalItems={totalItems} />}
        listSearch={
          <ListSearch
            filtersOpen={filtersOpen}
            setFiltersOpen={setFiltersOpen}
            onSearch={() => setDefaultItemsPerPage(new URLSearchParams(searchParams), DEFAULT_ITEMS_PER_PAGE)}
          />
        }
        searchFilters={
          <SearchFilters
            displayActiveFiltersAsTiles={!filtersOpen}
            filterGroups={deviceFilterGroups}
            onModalClose={() => setFiltersOpen(false)}
          />
        }
      />
    </div>
  );
};
